import {
  canConnect,
  defaultConfig,
  type Endpoint,
  fittingSource,
  type FlowGraph,
  getNodeDefinition,
  validateGraph,
} from '@reelplicate/flows';

const GUARDRAIL = 'guardrail.brandGuardrails';

/** A readable id unique in the graph: "hookVariations", then "hookVariations-2". */
export function nextNodeId(graph: FlowGraph, type: string): string {
  const base = type.split('.').pop() ?? 'node';
  const taken = new Set(graph.nodes.map((node) => node.id));
  if (!taken.has(base)) return base;
  let n = 2;
  while (taken.has(`${base}-${n}`)) n++;
  return `${base}-${n}`;
}

const edgeId = (source: Endpoint, target: Endpoint) =>
  `${source.nodeId}:${source.port}->${target.nodeId}:${target.port}`;

/** Drops a node of `type` at `position` with its default settings. */
export function addNode(
  graph: FlowGraph,
  type: string,
  position: { x: number; y: number },
): { graph: FlowGraph; id: string } {
  const definition = getNodeDefinition(type);
  if (!definition) throw new Error(`Unknown node type ${type}`);
  const id = nextNodeId(graph, type);
  return {
    id,
    graph: {
      ...graph,
      nodes: [
        ...graph.nodes,
        {
          id,
          type,
          typeVersion: definition.version,
          position,
          config: defaultConfig(definition),
        },
      ],
    },
  };
}

export type ConnectResult =
  { ok: true; graph: FlowGraph; guardrailId?: string } | { ok: false; reason: string };

/**
 * Draws an edge. When it would let content reach Schedule & publish without
 * Brand guardrails, a guardrail node is put on the edge instead - the board's
 * rule: "missing = auto-inserted with a warning".
 */
export function connect(
  graph: FlowGraph,
  dragged: Endpoint,
  target: Endpoint,
): ConnectResult {
  // Dragged from an output that doesn't fit, a sibling output that does is used.
  const source = fittingSource(graph, dragged, target);
  if (!source) {
    const refused = canConnect(graph, dragged, target);
    return refused.ok ? { ok: false, reason: 'Can’t connect these' } : refused;
  }
  const edge = { id: edgeId(source, target), source, target };
  const next = { ...graph, edges: [...graph.edges, edge] };

  const unguarded = (g: FlowGraph) =>
    validateGraph(g).some(
      (issue) => issue.code === 'MISSING_GUARDRAIL' && issue.nodeId === target.nodeId,
    );
  const sourceNode = graph.nodes.find((node) => node.id === source.nodeId);
  if (sourceNode?.type === GUARDRAIL || unguarded(graph) || !unguarded(next)) {
    return { ok: true, graph: next };
  }

  const from = sourceNode?.position ?? { x: 0, y: 0 };
  const to = graph.nodes.find((node) => node.id === target.nodeId)?.position ?? from;
  const added = addNode(graph, GUARDRAIL, {
    x: (from.x + to.x) / 2,
    y: (from.y + to.y) / 2 + 120,
  });
  const guard = { nodeId: added.id, port: 'in' };
  const first = canConnect(added.graph, source, guard);
  if (!first.ok) return { ok: true, graph: next }; // e.g. a type guardrails can't check
  const guardOut = { nodeId: added.id, port: 'out' };
  return {
    ok: true,
    guardrailId: added.id,
    graph: {
      ...added.graph,
      edges: [
        ...added.graph.edges,
        { id: edgeId(source, guard), source, target: guard },
        { id: edgeId(guardOut, target), source: guardOut, target },
      ],
    },
  };
}

/** Removes nodes (with every edge touching them) and edges. */
export function removeElements(
  graph: FlowGraph,
  nodeIds: readonly string[],
  edgeIds: readonly string[] = [],
): FlowGraph {
  const nodes = new Set(nodeIds);
  const edges = new Set(edgeIds);
  return {
    ...graph,
    nodes: graph.nodes.filter((node) => !nodes.has(node.id)),
    edges: graph.edges.filter(
      (edge) =>
        !edges.has(edge.id) &&
        !nodes.has(edge.source.nodeId) &&
        !nodes.has(edge.target.nodeId),
    ),
  };
}

export function moveNodes(
  graph: FlowGraph,
  positions: ReadonlyMap<string, { x: number; y: number }>,
): FlowGraph {
  return {
    ...graph,
    nodes: graph.nodes.map((node) => {
      const position = positions.get(node.id);
      return position ? { ...node, position } : node;
    }),
  };
}

export function updateNode(
  graph: FlowGraph,
  nodeId: string,
  change: { config?: Record<string, unknown>; label?: string | undefined },
): FlowGraph {
  return {
    ...graph,
    nodes: graph.nodes.map((node) => {
      if (node.id !== nodeId) return node;
      const next = {
        ...node,
        ...('config' in change ? { config: change.config ?? {} } : {}),
      };
      if ('label' in change) {
        const label = change.label?.trim();
        if (label) next.label = label;
        else delete next.label;
      }
      return next;
    }),
  };
}
